import { useState, useEffect, useContext } from 'react';
import { UserContext } from '~contexts/user';
import LocationContext from '../../locationContext';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useDefaultWish } from '~containers/wishes/default-wish';
import { usePersistedFilterState } from '~components/listing/useFilterPersistence';

export const useFilters = ({ cacheKeyParts = [], persistFilters = true } = {}) => {
  const { isLoadingLocation, pricingDetails } = useContext(LocationContext);
  const { user, isLoading: isLoadingUser, isLoaded: isLoadedUser } = useContext(UserContext);
  const { isAuthenticated, isLoading: isLoadingAuthentication } = useAuth0();
  const { wish } = user;
  const { wish: intakeWish } = useSelector((state) => state.intake);


  const defaultWish = useDefaultWish();

  const defaultFilters = {
    price: null,
    languages: wish?.languages?.length ? wish.languages : intakeWish?.languages?.length ? intakeWish.languages : defaultWish.languages,
    days: wish?.timeframes?.length ? wish.timeframes : intakeWish?.timeframes?.length ? intakeWish.timeframes : defaultWish.timeframes,
    attributes: wish?.attributes ? [...new Set(wish.attributes)] : intakeWish?.attributes ? [...new Set(intakeWish.attributes)] : defaultWish.attributes,
  };

  const { filters, setFilters, touched, setTouched, wasRestored } = usePersistedFilterState({
    cacheKeyParts,
    defaultFilters,
    enabled: persistFilters,
  });
  const [isInitializedLocation, setIsInitializedLocation] = useState(false);
  const [isInitializedWish, setIsInitializedWish] = useState(false);

  const updateFilters = (newFilters) => {
    setTouched(true);
    setFilters(newFilters);
  };

  const resetFilters = () => {
    setTouched(true);
    setFilters(defaultFilters);
  };

  useEffect(() => {
    if (isLoadingLocation || !isInitializedWish) {
      return;
    }

    // Restored selections keep their price; only an out-of-range value is re-defaulted.
    const outOfRange = pricingDetails && filters.price !== null &&
        (pricingDetails.minPrice > filters.price || pricingDetails.maxPrice < filters.price);

    if ((!wasRestored() && (!isInitializedLocation || !pricingDetails)) || outOfRange) {
      setFilters({
        ...filters,
        price: wish?.maximumRate.amount || pricingDetails?.maxPrice,
      });
    }

    setIsInitializedLocation(true);
  }, [isLoadingLocation, isInitializedWish]);

  useEffect(() => {
    if(isLoadingAuthentication || isLoadingUser) {
      return;
    }

    if(!isAuthenticated) {
      setIsInitializedWish(true);
      return;
    }

    if(!isLoadedUser) {
      return;
    }

    if(!wish) {
      setIsInitializedWish(true);
      return;
    }

    // Restored selections win over the wish-derived defaults.
    if (wasRestored()) {
      setIsInitializedWish(true);
      return;
    }

    setFilters({
      ...filters,
      languages: wish?.languages?.length ? wish.languages : defaultWish.languages,
      days: wish?.timeframes?.length ? wish.timeframes : defaultWish.timeframes,
      attributes: wish?.attributes ? [...new Set(wish.attributes)] : defaultWish.attributes,
    });
    setIsInitializedWish(true);
  }, [wish, isLoadingUser, isAuthenticated, isLoadedUser, isLoadingAuthentication]);

  return { filters, updateFilters, resetFilters, areFiltersInitialized: isInitializedLocation && isInitializedWish, filtersTouched: touched };
};
